import { z } from 'zod';
import { EnteredNameSchema, NoteSchema, PhoneSchema, RecordIdSchema } from './identifiers';
import { LocaleSchema } from './localized';
import { MoneySchema } from './money';
import { PropertyTypeSchema, UsageTypeSchema } from './inventory';
import { ArchiveMarkSchema, ArchiveViewSchema } from './lifecycle';
import { BusinessDateSchema, InstantSchema, LocalTimeSchema } from './time';

/**
 * CRM — `CRM-*` demonstration slice (ADR-0025).
 *
 * A **lead** is an interest; a **customer** is a person the business has a relationship with. They are
 * separate records because a lead that goes nowhere must not leave a customer behind, and a customer
 * who buys twice must not be two leads. Converting a lead creates or links a customer and keeps the
 * lead as history.
 *
 * Names, phone numbers and notes are **user-entered content**: stored exactly as entered, never
 * translated, never reformatted, and rendered direction-isolated (ADR-0003, I18N-009). A phone number
 * is additionally normalized to digits for duplicate detection, and the normalized form never replaces
 * what was typed.
 */

/* -------------------------------------------------------------------- customer */

/** CRM-PERSON-001: a person or a company. Records written before BMP-1 carry no kind and read as `individual`. */
export const CUSTOMER_KINDS = ['individual', 'company'] as const;
export const CustomerKindSchema = z.enum(CUSTOMER_KINDS);
export type CustomerKind = z.infer<typeof CustomerKindSchema>;

/** CRM-PERSON-002. Which identifier is mandatory, and when, is `BD-34`; none is required here. */
export const IDENTITY_TYPES = [
  'nationalId',
  'passport',
  'commercialRegistration',
  'taxNumber',
] as const;
export const IdentityTypeSchema = z.enum(IDENTITY_TYPES);
export type IdentityType = z.infer<typeof IdentityTypeSchema>;

/**
 * An identity document number, as entered. Letters, digits, spaces and hyphens only — never
 * reformatted, displayed verbatim and isolated (ADR-0003), and matched for duplicates on its
 * letters and digits alone.
 */
export const CustomerIdentitySchema = z.strictObject({
  type: IdentityTypeSchema,
  number: z
    .string()
    .trim()
    .min(3)
    .max(40)
    .regex(/^[A-Za-z0-9][A-Za-z0-9 -]*$/, { message: 'IDENTITY_NUMBER_EXPECTED' }),
  /** ISO 3166-1 alpha-2, for a passport. */
  issuingCountry: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
});
export type CustomerIdentity = z.infer<typeof CustomerIdentitySchema>;

/** Letters and digits of an identity number, upper-cased — the form duplicates are matched on. */
export function normalizeIdentityNumber(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** An e-mail address in the form duplicates are matched on. The entered form is what is displayed. */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/** CRM-PERSON-003. The channels a customer can be contacted through, and consent is recorded for. */
export const CONTACT_CHANNELS = ['phone', 'whatsapp', 'sms', 'email'] as const;
export const ContactChannelSchema = z.enum(CONTACT_CHANNELS);
export type ContactChannel = z.infer<typeof ContactChannelSchema>;

/** How a consent was captured. The wording and the mandatory mechanism are `SD-09` / `BD-21`. */
export const CONSENT_SOURCES = ['verbal', 'writtenForm', 'onlineForm', 'import'] as const;
export const ConsentSourceSchema = z.enum(CONSENT_SOURCES);

/** The consent in force for one channel: the latest entry of an append-only history. */
export const ConsentStateSchema = z.strictObject({
  channel: ContactChannelSchema,
  granted: z.boolean(),
  source: ConsentSourceSchema,
  recordedAt: InstantSchema,
  recordedBy: z.string().min(1).max(200),
});
export type ConsentState = z.infer<typeof ConsentStateSchema>;

export const CustomerSchema = z.strictObject({
  customerId: RecordIdSchema,
  kind: CustomerKindSchema,
  name: EnteredNameSchema,
  /** The name in the other script, when the customer uses one — entered, never transliterated. */
  alternateName: EnteredNameSchema.optional(),
  primaryPhone: PhoneSchema,
  secondaryPhone: PhoneSchema.optional(),
  email: z.string().trim().email().max(254).optional(),
  /**
   * Field-restricted (SEC-029): absent for an actor without `crm.customer.viewIdentity`, on every read,
   * list and export. A record written before BMP-1 exposes its legacy national ID here.
   */
  identity: CustomerIdentitySchema.optional(),
  address: z.string().trim().max(400).optional(),
  city: z.string().trim().max(80).optional(),
  preferredLanguage: LocaleSchema.optional(),
  preferredChannel: ContactChannelSchema.optional(),
  /** Consent in force per channel. A channel with no entry has no recorded consent. */
  consents: z.array(ConsentStateSchema),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  /** The sales owner. `self`- and `assigned`-scoped actors resolve against this. */
  ownerAccountId: z.string().min(1).max(200),
  /**
   * Set when this record was merged into another (CRM-PERSON-006): it stays, read-only, so every
   * record made under it keeps its identity; new work goes to the survivor.
   */
  mergedInto: z
    .strictObject({ customerId: RecordIdSchema, mergeId: RecordIdSchema, mergedAt: InstantSchema })
    .optional(),
  /** The records merged into this one — their own records stay under their identifiers. */
  mergedFrom: z.array(
    z.strictObject({
      customerId: RecordIdSchema,
      name: z.string().max(200),
      mergeId: RecordIdSchema,
      mergedAt: InstantSchema,
    }),
  ),
  /** Set while archived (ADR-0047): hidden from active lists and new work, history intact. */
  archived: ArchiveMarkSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Customer = z.infer<typeof CustomerSchema>;

/* ------------------------------------------------------------ customer merge (CRM-PERSON-006) */

export const CUSTOMER_MERGE_BLOCKERS = [
  'sameCustomer',
  'alreadyMerged',
  'survivorMerged',
  'otherLegalEntity',
  'notVisible',
  /** One of the two is archived (ADR-0047): restore it first, then merge. */
  'archived',
] as const;
export type CustomerMergeBlocker = (typeof CUSTOMER_MERGE_BLOCKERS)[number];

export const CustomerMergePreviewSchema = z.strictObject({
  survivorId: RecordIdSchema,
  duplicateId: RecordIdSchema,
  survivorName: z.string().max(200),
  duplicateName: z.string().max(200).optional(),
  blockers: z.array(z.enum(CUSTOMER_MERGE_BLOCKERS)),
  /** What will appear on the survivor's page, each record keeping its own customer identifier. */
  counts: z.strictObject({
    leads: z.number().int().nonnegative(),
    opportunities: z.number().int().nonnegative(),
    activities: z.number().int().nonnegative(),
    reservations: z.number().int().nonnegative(),
    contracts: z.number().int().nonnegative(),
    /** Consents are never carried: each person's consent stays with the record it was given on. */
    consentsNotCarried: z.number().int().nonnegative(),
    /** Customers previously merged into the duplicate, merged along with it. */
    earlierAliases: z.number().int().nonnegative(),
  }),
});
export type CustomerMergePreview = z.infer<typeof CustomerMergePreviewSchema>;

export const MergeCustomersSchema = z.strictObject({
  duplicateId: RecordIdSchema,
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().min(8).max(200),
});
export type MergeCustomers = z.infer<typeof MergeCustomersSchema>;

export const CustomerMergePreviewQuerySchema = z.strictObject({ duplicateId: RecordIdSchema });

export const CreateCustomerSchema = z.strictObject({
  kind: CustomerKindSchema.default('individual'),
  name: EnteredNameSchema,
  alternateName: EnteredNameSchema.optional(),
  primaryPhone: PhoneSchema,
  secondaryPhone: PhoneSchema.optional(),
  email: z.string().trim().email().max(254).optional(),
  identity: CustomerIdentitySchema.optional(),
  address: z.string().trim().max(400).optional(),
  city: z.string().trim().max(80).optional(),
  preferredLanguage: LocaleSchema.optional(),
  preferredChannel: ContactChannelSchema.optional(),
  branchId: RecordIdSchema,
  /** Naming another owner needs `crm.customer.transfer`; without it the creator owns the customer. */
  ownerAccountId: z.string().min(1).max(200).optional(),
});
export type CreateCustomer = z.input<typeof CreateCustomerSchema>;

/**
 * CRM-PERSON-005: a correction. It states why and the version it read; a stale version is a
 * conflict, never a silent overwrite. Owner, branch and legal entity are not corrected here — the
 * owner moves by transfer, and the placement is fixed by the records that depend on it.
 */
export const UpdateCustomerSchema = z.strictObject({
  kind: CustomerKindSchema.optional(),
  name: EnteredNameSchema.optional(),
  alternateName: EnteredNameSchema.optional(),
  primaryPhone: PhoneSchema.optional(),
  secondaryPhone: PhoneSchema.optional(),
  email: z.string().trim().email().max(254).optional(),
  identity: CustomerIdentitySchema.optional(),
  address: z.string().trim().max(400).optional(),
  city: z.string().trim().max(80).optional(),
  preferredLanguage: LocaleSchema.optional(),
  preferredChannel: ContactChannelSchema.optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type UpdateCustomer = z.infer<typeof UpdateCustomerSchema>;

export const RecordConsentSchema = z.strictObject({
  channel: ContactChannelSchema,
  granted: z.boolean(),
  source: ConsentSourceSchema,
  note: z.string().trim().max(500).optional(),
});
export type RecordConsent = z.infer<typeof RecordConsentSchema>;

/** CRM-OWNER-001: hand a customer to another owner, with a reason kept in the history. */
export const TransferOwnershipSchema = z.strictObject({
  toAccountId: z.string().min(1).max(200),
  reason: z.string().trim().min(3).max(500),
});
export type TransferOwnership = z.infer<typeof TransferOwnershipSchema>;

export const CUSTOMER_PAGE_SIZE_DEFAULT = 25;
export const CUSTOMER_PAGE_SIZE_MAX = 100;

export const CustomerQuerySchema = z.strictObject({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(CUSTOMER_PAGE_SIZE_MAX)
    .default(CUSTOMER_PAGE_SIZE_DEFAULT),
  cursor: z.string().min(1).max(200).optional(),
  /** Anchored prefix match on the name or the phone digits. Never a regular expression. */
  search: z.string().trim().max(80).optional(),
  kind: CustomerKindSchema.optional(),
  branchId: RecordIdSchema.optional(),
  ownerAccountId: z.string().min(1).max(200).optional(),
  /** Active records (the default), archived ones, or both (ADR-0047). */
  archive: ArchiveViewSchema.optional(),
  /**
   * Comma-separated customer identifiers, at most one hundred: how a list of quotations or contracts
   * names its customers in one request. Still scoped — an identifier outside the actor's scope is
   * simply absent from the answer.
   */
  ids: z
    .string()
    .regex(
      /^[a-z][a-z0-9]{0,11}_[A-Za-z0-9]{6,64}(,[a-z][a-z0-9]{0,11}_[A-Za-z0-9]{6,64}){0,99}$/,
      {
        message: 'RECORD_IDS_EXPECTED',
      },
    )
    .optional(),
});
export type CustomerQuery = z.infer<typeof CustomerQuerySchema>;

export const CustomerPageSchema = z.strictObject({
  items: z.array(CustomerSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type CustomerPage = z.infer<typeof CustomerPageSchema>;

/** What a duplicate matched on. */
export const DUPLICATE_MATCHES = ['phone', 'email', 'identity'] as const;
export const DuplicateMatchSchema = z.enum(DUPLICATE_MATCHES);

/**
 * CRM-PERSON-006. Candidates are shown **only inside the actor's scope**; matches outside it are
 * counted, never described, so a duplicate check cannot become a way to read another branch's
 * customers. Merging is not performed: the rule is `BD-26`.
 */
export const DuplicateReportSchema = z.strictObject({
  candidates: z.array(
    z.strictObject({
      customerId: RecordIdSchema,
      name: EnteredNameSchema,
      matchedOn: z.array(DuplicateMatchSchema).min(1),
      /** An archived customer still holds its phone: the way back is to restore it (ADR-0047). */
      archived: z.boolean().optional(),
    }),
  ),
  outOfScopeMatches: z.number().int().nonnegative(),
});
export type DuplicateReport = z.infer<typeof DuplicateReportSchema>;

export const DuplicateCheckSchema = z
  .strictObject({
    primaryPhone: PhoneSchema.optional(),
    email: z.string().trim().email().max(254).optional(),
    identityNumber: z.string().trim().min(3).max(40).optional(),
    branchId: RecordIdSchema,
  })
  .refine((value) => value.primaryPhone ?? value.email ?? value.identityNumber, {
    message: 'NOTHING_TO_CHECK',
  });
export type DuplicateCheck = z.infer<typeof DuplicateCheckSchema>;

/** One ownership change, append-only (CRM-OWNER-001). */
export const OwnershipChangeSchema = z.strictObject({
  changeId: RecordIdSchema,
  subjectType: z.enum(['customer', 'lead', 'opportunity']),
  subjectId: RecordIdSchema,
  fromAccountId: z.string().max(200).optional(),
  toAccountId: z.string().min(1).max(200),
  reason: z.string().max(500),
  actorAccountId: z.string().min(1).max(200),
  occurredAt: InstantSchema,
});
export type OwnershipChange = z.infer<typeof OwnershipChangeSchema>;
export const OwnershipHistorySchema = z.strictObject({ items: z.array(OwnershipChangeSchema) });

/* ------------------------------------------------------------------------ lead */

/**
 * Where the lead came from. Facebook, Instagram and WhatsApp appear here as **recorded origins**, not
 * as live integrations: nothing is connected, and a lead from one of them was entered by a person or
 * imported (ADR-0026).
 */
export const LEAD_SOURCES = [
  'facebook',
  'instagram',
  'whatsapp',
  'website',
  // Shown as "Hotline — الخط الساخن" since 2026-10-26; the stored code is unchanged so history reads.
  'phoneCall',
  'walkIn',
  'referral',
  'broker',
  /** A personal contact of the salesperson (2026-10-26) — distinct from "other". */
  'personal',
  'other',
] as const;
export const LeadSourceSchema = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof LeadSourceSchema>;

export const LEAD_STAGES = [
  'new',
  'contacted',
  'qualified',
  'visitScheduled',
  'negotiation',
  'reservation',
  'won',
  'lost',
] as const;
export const LeadStageSchema = z.enum(LEAD_STAGES);
export type LeadStage = z.infer<typeof LeadStageSchema>;

/**
 * Permitted pipeline moves.
 *
 * Deliberately permissive in the middle — a real pipeline goes backwards when a deal cools, and a
 * system that forbids it just teaches people to lie in the notes. The two terminal stages are the
 * strict ones: `won` and `lost` are conclusions, and reopening is an explicit move back to
 * `contacted` rather than a silent edit.
 */
export const LEAD_TRANSITIONS: Readonly<Record<LeadStage, readonly LeadStage[]>> = {
  new: ['contacted', 'qualified', 'lost'],
  contacted: ['qualified', 'visitScheduled', 'negotiation', 'lost'],
  qualified: ['contacted', 'visitScheduled', 'negotiation', 'lost'],
  visitScheduled: ['contacted', 'qualified', 'negotiation', 'reservation', 'lost'],
  negotiation: ['qualified', 'visitScheduled', 'reservation', 'lost'],
  reservation: ['negotiation', 'won', 'lost'],
  won: [],
  lost: ['contacted'],
};

export function canTransitionLead(from: LeadStage, to: LeadStage): boolean {
  return LEAD_TRANSITIONS[from].includes(to);
}

/** A stage that is over. Used for conversion rates and to require a reason on the way out. */
export const TERMINAL_LEAD_STAGES: readonly LeadStage[] = ['won', 'lost'];

/**
 * The interest a lead shows, as a salesperson reads it (2026-10-22). Not a second status: each is a
 * fixed group of pipeline stages, so filters, counts and drill-downs read the same stored stage.
 * `won` is reached only through a contract's activation or a recorded manual win with a reason —
 * choosing "interested" never marks a sale.
 */
export const LEAD_INTEREST_STATES = [
  'new',
  'followUp',
  'interested',
  'notInterested',
  'reserving',
  'won',
] as const;
export const LeadInterestSchema = z.enum(LEAD_INTEREST_STATES);
export type LeadInterest = z.infer<typeof LeadInterestSchema>;

export const LEAD_INTEREST_STAGES: Readonly<Record<LeadInterest, readonly LeadStage[]>> = {
  new: ['new'],
  followUp: ['contacted'],
  interested: ['qualified', 'visitScheduled', 'negotiation'],
  notInterested: ['lost'],
  reserving: ['reservation'],
  won: ['won'],
};

export function leadInterestOf(stage: LeadStage): LeadInterest {
  for (const interest of LEAD_INTEREST_STATES) {
    if (LEAD_INTEREST_STAGES[interest].includes(stage)) return interest;
  }
  return 'new';
}

/** The statuses a new lead may start in: new, needs follow-up, interested, or not interested. */
export const INITIAL_LEAD_STAGES = ['new', 'contacted', 'qualified', 'lost'] as const;

/* -------------------------------------------------------- follow-up status */

/**
 * The one status a salesperson sets and reads on a lead (2026-10-26): "حالة المتابعة". It replaces
 * the separate stage and interest-group controls on screen; the stored pipeline `stage` remains the
 * system's state (open or closed, contract outcomes) and moves with it only where the meaning is
 * unambiguous.
 *
 * - `booked` — a confirmed reservation exists for this lead. Set only by the reservation workflow,
 *   never by hand; the owner's definition of a won sale.
 * - `legacyWon` — a lead marked won by hand before 2026-10-26 (no reservation here). Kept apart;
 *   never set again and never turned into `booked`.
 * - `opened` — an open sales opportunity (not "a message was opened"). `timed` — a phone call
 *   scheduled at a date and time. `contactInFuture` — follow up later, on a date. `waiting` — waiting
 *   for something, said in a few words. `lowBudget` is an objection, not a closure; `wrongNumber`
 *   records the result and never changes the phone number; `noAnswer` records an attempt and never
 *   closes or recycles anything by itself. `lost` and `notInterested` close the lead, with a reason.
 */
export const FOLLOW_UP_STATUSES = [
  'new',
  'noAnswer',
  'opened',
  'interested',
  'negotiation',
  'visited',
  'timed',
  'booked',
  'contactInFuture',
  'waiting',
  'lost',
  'lowBudget',
  'wrongNumber',
  'notInterested',
  'legacyWon',
] as const;
export const FollowUpStatusSchema = z.enum(FOLLOW_UP_STATUSES);
export type FollowUpStatus = z.infer<typeof FollowUpStatusSchema>;

/** The status menu's groups, in the order shown. */
export const FOLLOW_UP_POSITIVE: readonly FollowUpStatus[] = [
  'opened',
  'interested',
  'negotiation',
  'visited',
  'timed',
  'booked',
  'contactInFuture',
  'waiting',
];
export const FOLLOW_UP_NEGATIVE: readonly FollowUpStatus[] = [
  'lost',
  'lowBudget',
  'wrongNumber',
  'notInterested',
];
export const FOLLOW_UP_NEUTRAL: readonly FollowUpStatus[] = ['new', 'noAnswer'];
export const FOLLOW_UP_CATEGORIES = ['positive', 'negative', 'neutral'] as const;
export type FollowUpCategory = (typeof FOLLOW_UP_CATEGORIES)[number];

export function followUpCategory(status: FollowUpStatus): FollowUpCategory {
  if (FOLLOW_UP_POSITIVE.includes(status) || status === 'legacyWon') return 'positive';
  if (FOLLOW_UP_NEGATIVE.includes(status)) return 'negative';
  return 'neutral';
}

/** Statuses only the system records: a booking, the legacy manual win, and a lead's first state. */
export const SYSTEM_FOLLOW_UP_STATUSES: readonly FollowUpStatus[] = ['booked', 'legacyWon', 'new'];
/** Statuses a person may choose. */
export const SETTABLE_FOLLOW_UP_STATUSES = FOLLOW_UP_STATUSES.filter(
  (status) => !SYSTEM_FOLLOW_UP_STATUSES.includes(status),
) as Exclude<FollowUpStatus, 'booked' | 'legacyWon' | 'new'>[];
/** A closed follow-up: sold (booked, legacy win) or concluded without a sale. */
export const CLOSED_FOLLOW_UP_STATUSES: readonly FollowUpStatus[] = [
  'booked',
  'legacyWon',
  'lost',
  'notInterested',
];
/** Statuses that need a reason written down. */
export const REASONED_FOLLOW_UP_STATUSES: readonly FollowUpStatus[] = ['lost', 'notInterested'];
/** Statuses that need a date (and `timed` a time) to mean anything. */
export const SCHEDULED_FOLLOW_UP_STATUSES: readonly FollowUpStatus[] = ['timed', 'contactInFuture'];
/** Statuses that count as an attempt to reach the customer where nobody was reached. */
export const UNREACHED_FOLLOW_UP_STATUSES: readonly FollowUpStatus[] = ['noAnswer', 'wrongNumber'];

/**
 * The pseudo-status a list filter uses for a lead whose legacy stage could not be mapped without
 * guessing — shown for a person to correct, never silently made won or lost.
 */
export const UNMAPPED_FOLLOW_UP = 'unmapped' as const;

/**
 * The deterministic mapping of a lead recorded before 2026-10-26 (stage, outcome, follow-up date)
 * to its follow-up status — `undefined` where it cannot be decided without inventing a call, a
 * visit or a booking; such a lead stays visible as "needs a status" for a person to correct.
 *
 * - `new` → New · `qualified`, `visitScheduled` → Interested (the "interested" group they were
 *   shown in) · `negotiation` → Negotiation · `contacted` with a follow-up date → Contact in future
 * - `lost` → Lost (its reason kept), or Lost after a cancelled contract
 * - `won` by an activated contract → Booked (a contract here was made from a confirmed reservation)
 * - `won` by hand, or concluded before outcomes were recorded → Legacy won
 * - `contacted` without a date, `reservation` (not confirmed) → needs a person's decision
 */
export function mapLegacyFollowUpStatus(lead: {
  stage: LeadStage;
  nextFollowUpOn?: string | undefined;
  outcome?: { kind: string } | undefined;
}): FollowUpStatus | undefined {
  switch (lead.stage) {
    case 'new':
      return 'new';
    case 'qualified':
    case 'visitScheduled':
      return 'interested';
    case 'negotiation':
      return 'negotiation';
    case 'contacted':
      return lead.nextFollowUpOn ? 'contactInFuture' : undefined;
    case 'lost':
      return 'lost';
    case 'won':
      return lead.outcome?.kind === 'contract' || lead.outcome?.kind === 'reservation'
        ? 'booked'
        : 'legacyWon';
    case 'reservation':
      return undefined;
  }
}

/**
 * Where the stored pipeline stage goes when a person sets a follow-up status — forward only, and
 * only when the meaning is clear. `undefined`: the stage stays as it is.
 */
export function stageForFollowUpStatus(
  status: FollowUpStatus,
  current: LeadStage,
): LeadStage | undefined {
  const reopened = current === 'lost';
  const early = current === 'new' || current === 'contacted' || reopened;
  switch (status) {
    case 'lost':
    case 'notInterested':
      return current === 'lost' ? undefined : 'lost';
    case 'opened':
    case 'interested':
    case 'lowBudget':
      return early ? (reopened ? 'contacted' : 'qualified') : undefined;
    case 'negotiation':
      return reopened ? 'contacted' : current === 'negotiation' ? undefined : 'negotiation';
    case 'visited':
      return reopened
        ? 'contacted'
        : current === 'new' || current === 'contacted' || current === 'qualified'
          ? 'visitScheduled'
          : undefined;
    case 'noAnswer':
    case 'wrongNumber':
    case 'timed':
    case 'contactInFuture':
    case 'waiting':
      return current === 'new' || reopened ? 'contacted' : undefined;
    default:
      return undefined;
  }
}

/** Who may still work a lead: Recycle waits for reassignment; Cold Call is renewed outreach. */
/** What a scheduled next follow-up is: the Sales Admin's meeting and visit counts read this. */
export const FOLLOW_UP_KINDS = ['call', 'callback', 'meeting', 'siteVisit', 'followUp'] as const;
export const FollowUpKindSchema = z.enum(FOLLOW_UP_KINDS);
export type FollowUpKind = z.infer<typeof FollowUpKindSchema>;

/**
 * A recorded contact's result. `reached` is a successful contact; `noAnswer` and `wrongNumber` are
 * attempts nobody answered; `notHeld` is a meeting or visit that did not happen. Staff-recorded, and
 * shown as such: nothing here is verified by telephony.
 */
export const CONTACT_RESULTS = ['reached', 'noAnswer', 'wrongNumber', 'notHeld'] as const;
export const ContactResultSchema = z.enum(CONTACT_RESULTS);
export type ContactResult = z.infer<typeof ContactResultSchema>;

export const LEAD_WORK_CATEGORIES = ['recycle', 'coldCall'] as const;
export const LeadWorkCategorySchema = z.enum(LEAD_WORK_CATEGORIES);
export type LeadWorkCategory = z.infer<typeof LeadWorkCategorySchema>;

/** CRM-LEAD-004. The facts a qualification records; none of them is a score the product invents. */
export const QUALIFICATION_TIMEFRAMES = [
  'immediate',
  'withinThreeMonths',
  'withinSixMonths',
  'later',
  'unknown',
] as const;
export const QUALIFICATION_PURPOSES = ['residence', 'investment', 'business', 'unknown'] as const;
export const DECISION_ROLES = ['decisionMaker', 'influencer', 'unknown'] as const;

export const LeadQualificationSchema = z.strictObject({
  budgetConfirmed: z.boolean(),
  timeframe: z.enum(QUALIFICATION_TIMEFRAMES),
  purpose: z.enum(QUALIFICATION_PURPOSES),
  decisionRole: z.enum(DECISION_ROLES),
  notes: NoteSchema.optional(),
  qualifiedAt: InstantSchema,
  qualifiedBy: z.string().min(1).max(200),
});
export type LeadQualification = z.infer<typeof LeadQualificationSchema>;

/** A reason code from the deployment's `lossReasons` reference list (CRM-LOSS-001). */
export const ReasonCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/, { message: 'REFERENCE_CODE_EXPECTED' });

/**
 * `reservation` — a confirmed reservation won it (2026-10-26, the owner's definition); its contract,
 * when one is activated later, is recorded on the same outcome. `reservationCancelled` — that
 * reservation ended before a contract. `contract` — a contract won it without a reservation win
 * recorded first (before 2026-10-26, or onboarded history).
 */
export const LEAD_OUTCOME_KINDS = [
  'manual',
  'contract',
  'contractCancelled',
  'reservation',
  'reservationCancelled',
] as const;

export const LeadSchema = z.strictObject({
  leadId: RecordIdSchema,
  /** Stable and readable (`LD-2026-00001`); it appears wherever staff talk about a lead. */
  leadNumber: z.string().max(40).optional(),
  name: EnteredNameSchema,
  primaryPhone: PhoneSchema,
  secondaryPhone: PhoneSchema.optional(),
  email: z.string().trim().email().max(254).optional(),
  /** The **original** source — immutable, so attribution to where the enquiry began survives (CRM-LEAD-002). */
  source: LeadSourceSchema,
  /** The source now credited, when it differs from the original. Every change is on the timeline. */
  currentSource: LeadSourceSchema.optional(),
  /** Opaque reference to a marketing campaign. No provider is connected (ADR-0026). */
  campaignId: RecordIdSchema.optional(),
  interestedProjectId: RecordIdSchema.optional(),
  preferredPropertyType: PropertyTypeSchema.optional(),
  preferredUsageType: UsageTypeSchema.optional(),
  budgetMin: MoneySchema.optional(),
  budgetMax: MoneySchema.optional(),
  /** The sales owner; the record's `assigned` scope resolves against this. */
  assignedToAccountId: z.string().min(1).max(200),
  /**
   * Set while nobody has been given the lead (ADR-0049): `assignedToAccountId` then only names who
   * holds it in the queue. Assigning it clears this; it is never inferred from a status.
   */
  unassignedSince: InstantSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  notes: NoteSchema.optional(),
  nextFollowUpOn: BusinessDateSchema.optional(),
  stage: LeadStageSchema,
  /** Required when the stage is `lost`; kept if the lead is later reopened. */
  lostReason: z.string().trim().max(500).optional(),
  /** The `lossReasons` reference code, when one was chosen. */
  lostReasonCode: ReasonCodeSchema.optional(),
  /**
   * How the lead concluded (2026-10-21): `contract` — a contract was activated for it; `manual` — a
   * person marked it won, with a reason, and no contract here backs it; `contractCancelled` — the
   * contract that won it was cancelled. Absent on leads concluded before, which reports count apart.
   */
  outcome: z
    .strictObject({
      kind: z.enum(LEAD_OUTCOME_KINDS),
      contractId: RecordIdSchema.optional(),
      reservationId: RecordIdSchema.optional(),
      /** When the contract of a reservation win was activated — a later milestone, kept apart. */
      contractActivatedAt: InstantSchema.optional(),
      reason: z.string().max(500).optional(),
      recordedAt: InstantSchema,
      recordedByAccountId: z.string(),
    })
    .optional(),
  /**
   * The follow-up status (2026-10-26). Absent only on a lead from before then whose old stage could
   * not be mapped without guessing: it is listed as needing a status until a person sets one.
   */
  followUpStatus: FollowUpStatusSchema.optional(),
  /** When `followUpStatus` came from the 2026-10-26 mapping, the stage it was mapped from. */
  followUpStatusMappedFrom: LeadStageSchema.optional(),
  /** The scheduled call's time of day (organization timezone), with `nextFollowUpOn`. */
  nextFollowUpTime: LocalTimeSchema.optional(),
  /** What the next follow-up is; absent on one scheduled before kinds existed. */
  nextFollowUpKind: FollowUpKindSchema.optional(),
  /** What a Waiting lead waits for, in a few words. */
  waitingFor: z.string().max(200).optional(),
  /** Visited: who recorded it, when, and the visit's own date where given. */
  visit: z
    .strictObject({
      recordedAt: InstantSchema,
      recordedByAccountId: z.string(),
      visitedOn: BusinessDateSchema.optional(),
    })
    .optional(),
  /** Attempts to reach the customer since the current assignment; and the last one's time. */
  contactAttempts: z.number().int().nonnegative().optional(),
  lastAttemptAt: InstantSchema.optional(),
  /** Consecutive attempts with no answer since the customer was last reached. */
  noAnswerStreak: z.number().int().nonnegative().optional(),
  /** When the customer was last actually reached. */
  lastContactAt: InstantSchema.optional(),
  /** When the current assignee received the lead (creation, assignment, recycle or cold call). */
  assignedAt: InstantSchema.optional(),
  /** Recycle (awaiting reassignment) or Cold Call (renewed outreach); absent for ordinary work. */
  workCategory: LeadWorkCategorySchema.optional(),
  recycle: z
    .strictObject({
      movedAt: InstantSchema,
      movedByAccountId: z.string(),
      reason: z.string().max(500),
      previousOwnerAccountId: z.string(),
      /** Set when the automatic rule moved it: the rule's settings version. */
      ruleVersion: z.number().int().positive().optional(),
    })
    .optional(),
  coldCall: z
    .strictObject({
      assignedAt: InstantSchema,
      assignedByAccountId: z.string(),
      reason: z.string().max(500),
    })
    .optional(),
  /** Kept for later contact after a loss or a pause (CRM-LOSS-001). */
  /** How the lead's first owner was chosen, when a distribution rule chose it (CRM-ASSIGN-002). */
  distribution: z.lazy(() => LeadDistributionSchema).optional(),
  nurture: z.boolean(),
  qualification: LeadQualificationSchema.optional(),
  /** Set once the lead becomes a customer. The lead itself is kept as history. */
  customerId: RecordIdSchema.optional(),
  convertedAt: InstantSchema.optional(),
  /** The last activity of any kind — what an ageing report reads (CRM-REPORT-001). */
  lastActivityAt: InstantSchema.optional(),
  /** Set while archived (ADR-0047): hidden from active lists and new work, history intact. */
  archived: ArchiveMarkSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Lead = z.infer<typeof LeadSchema>;

export const CreateLeadSchema = z.strictObject({
  name: EnteredNameSchema,
  primaryPhone: PhoneSchema,
  secondaryPhone: PhoneSchema.optional(),
  email: z.string().trim().email().max(254).optional(),
  source: LeadSourceSchema,
  campaignId: RecordIdSchema.optional(),
  interestedProjectId: RecordIdSchema.optional(),
  preferredPropertyType: PropertyTypeSchema.optional(),
  preferredUsageType: UsageTypeSchema.optional(),
  budgetMin: MoneySchema.optional(),
  budgetMax: MoneySchema.optional(),
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  /** Omitted means "assign to me". Naming someone else needs `crm.lead.assign`. */
  assignedToAccountId: z.string().min(1).max(200).optional(),
  /** Leave it for distribution: needs `crm.lead.assign`; excludes naming an assignee. */
  leaveUnassigned: z.boolean().optional(),
  notes: NoteSchema.optional(),
  nextFollowUpOn: BusinessDateSchema.optional(),
  /** The time of a scheduled call; only with `nextFollowUpOn`. */
  nextFollowUpTime: LocalTimeSchema.optional(),
  nextFollowUpKind: FollowUpKindSchema.optional(),
  /**
   * The status the lead starts in (2026-10-22); omitted is `new`. `lost` (not interested) needs
   * `initialStageReason`; `contacted` (follow-up needed) needs `nextFollowUpOn`.
   * Superseded by `initialStatus` (2026-10-26), still accepted.
   */
  initialStage: z.enum(INITIAL_LEAD_STAGES).optional(),
  initialStageReason: z.string().trim().max(500).optional(),
  /**
   * The follow-up status the lead starts in (2026-10-26); omitted is New. Only a status the person
   * chose asks for more: Timed a date and time, Contact in Future a date, Lost and Not Interested a
   * reason. Nothing else — no e-mail, no date — is required to save a lead.
   */
  initialStatus: z
    .enum(['new', ...SETTABLE_FOLLOW_UP_STATUSES] as [string, ...string[]])
    .optional(),
  waitingFor: z.string().trim().max(200).optional(),
});
export type CreateLead = z.infer<typeof CreateLeadSchema>;

/** Set a lead's follow-up status (2026-10-26). */
export const SetFollowUpStatusSchema = z.strictObject({
  status: z.enum(SETTABLE_FOLLOW_UP_STATUSES as [string, ...string[]]),
  /** Required for Lost and Not Interested. */
  reason: z.string().trim().max(500).optional(),
  /** A `lossReasons` code for Lost. */
  reasonCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/, { message: 'REFERENCE_CODE_EXPECTED' })
    .optional(),
  /** The follow-up date: required for Timed and Contact in Future, optional for Waiting. */
  followUpOn: BusinessDateSchema.optional(),
  /** The call's time: required for Timed. */
  followUpTime: LocalTimeSchema.optional(),
  waitingFor: z.string().trim().max(200).optional(),
  /** The visit's own date, for Visited. */
  visitedOn: BusinessDateSchema.optional(),
  note: NoteSchema.optional(),
  expectedVersion: z.number().int().positive().optional(),
});
export type SetFollowUpStatus = z.infer<typeof SetFollowUpStatusSchema>;

/** Move leads to Recycle, awaiting reassignment (2026-10-26). One result per lead, never skipped. */
export const RecycleLeadsSchema = z.strictObject({
  leadIds: z.array(RecordIdSchema).min(1).max(100),
  reason: z.string().trim().min(3).max(500),
  /** Retries with the same key return the same results. */
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type RecycleLeads = z.infer<typeof RecycleLeadsSchema>;

/** Assign recycled leads to a salesperson as Cold Call work, or reassign Cold Call work. */
export const ColdCallAssignSchema = z.strictObject({
  leadIds: z.array(RecordIdSchema).min(1).max(100),
  assignedToAccountId: z.string().min(1).max(200),
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type ColdCallAssign = z.infer<typeof ColdCallAssignSchema>;

export const LEAD_BULK_OUTCOMES = ['done', 'unchanged', 'refused'] as const;
export const LeadBulkResultSchema = z.strictObject({
  results: z.array(
    z.strictObject({
      leadId: z.string(),
      outcome: z.enum(LEAD_BULK_OUTCOMES),
      /** Why a lead was refused: a stable code shown in the person's language. */
      code: z.string().optional(),
    }),
  ),
  done: z.number().int().nonnegative(),
  refused: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
});
export type LeadBulkResult = z.infer<typeof LeadBulkResultSchema>;

/**
 * The automatic recycle rule (2026-10-26) — configured and switched on by a manager; nothing is
 * assumed. Off, it moves nothing. On, a lead qualifies when it is open, active, not already recycled,
 * in one of `statuses`, and either has `minNoAnswerAttempts` consecutive unanswered attempts or no
 * attempt for `inactivityDays` days counted from `timeBasis` — whichever is configured (at least one).
 * Exclusions: a future follow-up date, a lead kept for later contact, a lead linked to a live sale.
 * Moved leads go to `queueOwnerAccountId` (an eligible manager) awaiting reassignment.
 *
 * Eligible attempts are recorded calls, WhatsApp messages, e-mails, SMS, meetings, site visits, and
 * the No Answer and Wrong Number statuses. Opening a page or editing a field is not an attempt, and a
 * prepared manual WhatsApp reminder is not a delivery or an answer.
 */
export const AutoRecycleRuleSchema = z
  .strictObject({
    enabled: z.boolean(),
    statuses: z.array(FollowUpStatusSchema).min(1).max(FOLLOW_UP_STATUSES.length),
    minNoAnswerAttempts: z.number().int().min(1).max(50).nullable(),
    inactivityDays: z.number().int().min(1).max(365).nullable(),
    timeBasis: z.enum(['lastAttempt', 'assignment']),
    excludeFutureFollowUp: z.boolean(),
    excludeNurture: z.boolean(),
    queueOwnerAccountId: z.string().min(1).max(200),
  })
  .refine((rule) => rule.minNoAnswerAttempts !== null || rule.inactivityDays !== null, {
    message: 'AUTO_RECYCLE_CRITERION_REQUIRED',
  })
  .refine((rule) => !rule.statuses.some((status) => CLOSED_FOLLOW_UP_STATUSES.includes(status)), {
    message: 'AUTO_RECYCLE_CLOSED_STATUS',
  });
export type AutoRecycleRule = z.infer<typeof AutoRecycleRuleSchema>;

export const UpdateLeadSchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  secondaryPhone: PhoneSchema.optional(),
  email: z.string().trim().email().max(254).optional(),
  /** Re-credit the lead to another source. The original `source` never changes. */
  currentSource: LeadSourceSchema.optional(),
  interestedProjectId: RecordIdSchema.optional(),
  preferredPropertyType: PropertyTypeSchema.optional(),
  preferredUsageType: UsageTypeSchema.optional(),
  budgetMin: MoneySchema.optional(),
  budgetMax: MoneySchema.optional(),
  notes: NoteSchema.optional(),
  nextFollowUpOn: BusinessDateSchema.optional(),
  nurture: z.boolean().optional(),
  expectedVersion: z.number().int().positive().optional(),
});
export type UpdateLead = z.infer<typeof UpdateLeadSchema>;

export const ChangeLeadStageSchema = z.strictObject({
  stage: LeadStageSchema,
  /**
   * Mandatory for `lost` and for `won`: a person marking a lead won records why, because no contract
   * here backs it — a contract's activation wins the lead by itself.
   */
  reason: z.string().trim().max(500).optional(),
  /** A `lossReasons` code for `lost`; checked against the active reference items. */
  reasonCode: ReasonCodeSchema.optional(),
  /** Keep a lost lead for later contact. */
  nurture: z.boolean().optional(),
  expectedVersion: z.number().int().positive().optional(),
});
export type ChangeLeadStage = z.infer<typeof ChangeLeadStageSchema>;

export const QualifyLeadSchema = z.strictObject({
  budgetConfirmed: z.boolean(),
  timeframe: z.enum(QUALIFICATION_TIMEFRAMES),
  purpose: z.enum(QUALIFICATION_PURPOSES),
  decisionRole: z.enum(DECISION_ROLES),
  notes: NoteSchema.optional(),
  expectedVersion: z.number().int().positive().optional(),
});
export type QualifyLead = z.infer<typeof QualifyLeadSchema>;

export const AssignLeadSchema = z.strictObject({
  assignedToAccountId: z.string().min(1).max(200),
  reason: z.string().trim().min(3).max(500),
});
export type AssignLead = z.infer<typeof AssignLeadSchema>;

/* -------------------------------------------------------------------- activity */

export const ACTIVITY_KINDS = [
  'note',
  'call',
  'whatsapp',
  'meeting',
  'siteVisit',
  'followUpScheduled',
  'stageChanged',
  'assignmentChanged',
  'converted',
  'email',
  'sms',
  'qualified',
  'sourceChanged',
  'customerCorrected',
  'ownerChanged',
  'consentRecorded',
  /** The record was archived or restored, with the reason (ADR-0047). */
  'archived',
  'restored',
  /** The follow-up status changed (2026-10-26); `fromStage`/`toStage` carry the status codes. */
  'followUpStatusChanged',
  /** Moved to Recycle, or assigned from it as Cold Call work (2026-10-26). */
  'recycled',
  'coldCallAssigned',
] as const;
export const ActivityKindSchema = z.enum(ACTIVITY_KINDS);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;

/** The kinds a person records by hand; the rest are written by the system as things happen. */
export const MANUAL_ACTIVITY_KINDS = [
  'note',
  'call',
  'whatsapp',
  'email',
  'sms',
  'meeting',
  'siteVisit',
  'followUpScheduled',
] as const;

/**
 * Append-only. A timeline is what a sales manager reads to judge whether a record is being worked.
 * An activity belongs to a lead, a customer or an opportunity — at least one (CRM-ACTIVITY-001).
 */
export const ActivitySchema = z.strictObject({
  activityId: RecordIdSchema,
  leadId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  opportunityId: RecordIdSchema.optional(),
  kind: ActivityKindSchema,
  body: NoteSchema.optional(),
  /** Stage codes of a lead or an opportunity, as the timeline shows them. */
  fromStage: z.string().max(40).optional(),
  toStage: z.string().max(40).optional(),
  dueOn: BusinessDateSchema.optional(),
  /** With `dueOn` on a scheduling activity: the time and what was scheduled. */
  dueTime: LocalTimeSchema.optional(),
  scheduledKind: FollowUpKindSchema.optional(),
  /** A recorded outcome's result; absent on activities from before outcomes existed. */
  result: ContactResultSchema.optional(),
  /** The follow-up this activity completed: when it was due, and whether it was done in time. */
  completedFollowUp: z
    .strictObject({
      scheduledOn: BusinessDateSchema,
      scheduledTime: LocalTimeSchema.optional(),
      scheduledKind: FollowUpKindSchema.optional(),
      onTime: z.boolean(),
    })
    .optional(),
  actorAccountId: z.string().min(1).max(200).optional(),
  occurredAt: InstantSchema,
});
export type Activity = z.infer<typeof ActivitySchema>;

export const CreateActivitySchema = z.strictObject({
  kind: z.enum(MANUAL_ACTIVITY_KINDS),
  body: NoteSchema.optional(),
  /**
   * A follow-up date on the activity. On a `followUpScheduled` activity it always becomes the lead's
   * next follow-up; on any other kind only when `updateNextFollowUp` says so (UX-004).
   */
  dueOn: BusinessDateSchema.optional(),
  /** The time of the scheduled follow-up, and what it is; only with `dueOn`. */
  dueTime: LocalTimeSchema.optional(),
  scheduledKind: FollowUpKindSchema.optional(),
  /** Make `dueOn` the lead's next follow-up too — one date, not two that disagree. */
  updateNextFollowUp: z.boolean().optional(),
});
export type CreateActivity = z.infer<typeof CreateActivitySchema>;

/** The contact kinds "Record outcome" offers. */
export const OUTCOME_ACTIONS = [
  'call',
  'whatsapp',
  'email',
  'sms',
  'meeting',
  'siteVisit',
] as const;

/**
 * "Record outcome" (ADR-0049): after contacting a lead, one action says what happened and what is
 * next. A note alone never changes a status; `status` does, explicitly, under the same rules as the
 * status control, and an unanswered attempt records the status it means. Retrying with the same key
 * records nothing twice.
 */
export const RecordOutcomeSchema = z.strictObject({
  action: z.enum(OUTCOME_ACTIONS),
  result: ContactResultSchema,
  note: NoteSchema.optional(),
  status: z.enum(SETTABLE_FOLLOW_UP_STATUSES as [string, ...string[]]).optional(),
  /** Required when `status` is Lost or Not Interested. */
  reason: z.string().trim().max(500).optional(),
  /** False keeps the scheduled follow-up open: this contact did not complete it. */
  completesFollowUp: z.boolean().optional(),
  next: z
    .strictObject({
      on: BusinessDateSchema,
      time: LocalTimeSchema.optional(),
      kind: FollowUpKindSchema.optional(),
    })
    .optional(),
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type RecordOutcome = z.infer<typeof RecordOutcomeSchema>;

/* --------------------------------------------------------- queries and results */

export const LEAD_PAGE_SIZE_DEFAULT = 25;
export const LEAD_PAGE_SIZE_MAX = 100;

export const LeadQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(LEAD_PAGE_SIZE_MAX).default(LEAD_PAGE_SIZE_DEFAULT),
  cursor: z.string().min(1).max(200).optional(),
  stage: LeadStageSchema.optional(),
  /** A group of stages (2026-10-22); ignored when `stage` is given. */
  interest: LeadInterestSchema.optional(),
  /**
   * Follow-up statuses, comma-separated (2026-10-26); `unmapped` selects leads still needing one.
   * Dimensions combine with AND; values inside one dimension with OR.
   */
  statuses: z
    .string()
    .max(400)
    .transform((value, ctx) => {
      const parts = value.split(',').filter(Boolean);
      for (const part of parts) {
        if (
          part !== UNMAPPED_FOLLOW_UP &&
          !(FOLLOW_UP_STATUSES as readonly string[]).includes(part)
        ) {
          ctx.addIssue({ code: 'custom', message: 'UNKNOWN_FOLLOW_UP_STATUS' });
        }
      }
      return parts as (FollowUpStatus | typeof UNMAPPED_FOLLOW_UP)[];
    })
    .optional(),
  /** Current salespeople, comma-separated. A representative's own scope still applies. */
  assignees: z
    .string()
    .max(2100)
    .transform((value) => value.split(',').filter(Boolean).slice(0, 50))
    .optional(),
  /** Open (still being worked) or closed (booked, legacy won, lost, not interested). */
  followUpState: z.enum(['open', 'closed']).optional(),
  /** A work category, or `notRecycle`: everything not waiting in Recycle (the "move to Recycle" list). */
  workCategory: z.enum([...LEAD_WORK_CATEGORIES, 'notRecycle']).optional(),
  /** Leads created within a range, by the organization's calendar. */
  createdFrom: BusinessDateSchema.optional(),
  createdTo: BusinessDateSchema.optional(),
  source: LeadSourceSchema.optional(),
  assignedToAccountId: z.string().min(1).max(200).optional(),
  interestedProjectId: RecordIdSchema.optional(),
  branchId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  /** Anchored prefix match on the name or the phone digits. Never a regular expression. */
  search: z.string().trim().max(80).optional(),
  /**
   * The follow-up categories (2026-11-02). `overdue` is past its date and time; a date without a time
   * becomes overdue when the business day ends. `repeatedNoAnswer` and `noActivity` read their
   * configured thresholds and select nothing until those are set.
   */
  followUp: z
    .enum(['due', 'overdue', 'today', 'upcoming', 'callback', 'repeatedNoAnswer', 'noActivity'])
    .optional(),
  /** Who holds the lead: nobody yet, or a person (neither includes a lead waiting in Recycle). */
  assignment: z.enum(['unassigned', 'assigned']).optional(),
  /** What the next follow-up is: meetings and visits scheduled. */
  nextFollowUpKind: FollowUpKindSchema.optional(),
  preferredUsageType: UsageTypeSchema.optional(),
  preferredPropertyType: PropertyTypeSchema.optional(),
  /** Newest first unless named; any other order pages by position. */
  sort: z.enum(['createdAt', 'name', 'nextFollowUpOn']).optional(),
  direction: z.enum(['asc', 'desc']).optional(),
  /** Follow-ups scheduled within a date range, for the task calendar (UX-027). */
  followUpFrom: BusinessDateSchema.optional(),
  followUpTo: BusinessDateSchema.optional(),
  /** Active records (the default), archived ones, or both (ADR-0047). */
  archive: ArchiveViewSchema.optional(),
  customerId: RecordIdSchema.optional(),
  /** Leads linked to one marketing campaign (ADR-0042 attribution). */
  campaignId: RecordIdSchema.optional(),
  nurture: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});
export type LeadQuery = z.infer<typeof LeadQuerySchema>;

export const LeadPageSchema = z.strictObject({
  items: z.array(LeadSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type LeadPage = z.infer<typeof LeadPageSchema>;

/**
 * Created leads report a possible duplicate rather than refusing one.
 *
 * Two people really do share a phone number — a household, a company switchboard, a broker calling on
 * behalf of several buyers. Refusing the second lead would lose business; saying nothing would let the
 * same buyer be worked twice by two colleagues. So it is a warning attached to a successful create.
 */
export const CreateLeadResultSchema = z.strictObject({
  lead: LeadSchema,
  /**
   * The most recent open lead in the same legal entity sharing the phone or e-mail. Reported only
   * when it is inside the creator's scope; otherwise only `outOfScopeMatches` counts it.
   */
  possibleDuplicate: z
    .strictObject({
      leadId: RecordIdSchema,
      name: EnteredNameSchema,
      stage: LeadStageSchema,
      assignedToAccountId: z.string(),
      matchedOn: z.array(DuplicateMatchSchema).min(1),
      /** The matching lead is archived (ADR-0047): restoring it may be the right answer. */
      archived: z.boolean().optional(),
    })
    .optional(),
  /** An existing customer with the same phone or e-mail, inside the creator's scope. */
  existingCustomer: z
    .strictObject({
      customerId: RecordIdSchema,
      name: EnteredNameSchema,
      archived: z.boolean().optional(),
    })
    .optional(),
  outOfScopeMatches: z.number().int().nonnegative(),
});
export type CreateLeadResult = z.infer<typeof CreateLeadResultSchema>;

/**
 * A duplicate check before a lead is saved (UX-002): the same matching `createLead` reports after
 * saving, so the person decides before a second record exists. Nothing is stored.
 */
export const LeadDuplicateCheckSchema = z
  .strictObject({
    primaryPhone: PhoneSchema.optional(),
    email: z.string().trim().email().max(254).optional(),
    branchId: RecordIdSchema.optional(),
  })
  .refine((value) => value.primaryPhone ?? value.email, { message: 'NOTHING_TO_CHECK' });
export type LeadDuplicateCheck = z.infer<typeof LeadDuplicateCheckSchema>;

export const LeadDuplicateReportSchema = CreateLeadResultSchema.omit({ lead: true });
export type LeadDuplicateReport = z.infer<typeof LeadDuplicateReportSchema>;

/**
 * Age bands of **open** leads by creation date, in days (CRM-REPORT-001). They describe how old the
 * work is; they are not a service-level target, which no one has set (`SD-04`).
 */
export const LEAD_AGE_BANDS = ['upTo7', 'upTo30', 'upTo90', 'over90'] as const;

/** The CRM dashboard, computed inside the actor's scope by the same filter as the rows. */
/**
 * A period for the dashboard's lead figures (the sales report, 2026-10-23): leads **created** within
 * it, by the organization's calendar. Follow-ups due and overdue stay "as of today" whatever the period.
 */
export const CrmDashboardQuerySchema = z
  .strictObject({
    createdFrom: BusinessDateSchema.optional(),
    createdTo: BusinessDateSchema.optional(),
  })
  .refine(
    (query) => !query.createdFrom || !query.createdTo || query.createdFrom <= query.createdTo,
    { message: 'RANGE_INVERTED' },
  );
export type CrmDashboardQuery = z.infer<typeof CrmDashboardQuerySchema>;

export const CrmDashboardSchema = z.strictObject({
  totalLeads: z.number().int().nonnegative(),
  newLeads: z.number().int().nonnegative(),
  dueFollowUps: z.number().int().nonnegative(),
  overdueFollowUps: z.number().int().nonnegative(),
  wonLeads: z.number().int().nonnegative(),
  /** Of `wonLeads`: won by a confirmed reservation (the owner's definition, 2026-10-26). */
  wonByReservation: z.number().int().nonnegative(),
  /** Of `wonByReservation`: whose contract has since been activated — a later milestone. */
  reservationWinsWithContract: z.number().int().nonnegative(),
  /** Of `wonLeads`: won by an activated contract with no reservation win recorded first. */
  wonByContract: z.number().int().nonnegative(),
  /** Of `wonLeads`: marked won by a person, with a reason, without a contract here. */
  wonManual: z.number().int().nonnegative(),
  lostLeads: z.number().int().nonnegative(),
  /** Won ÷ (won + lost), as a decimal string. Zero when nothing has concluded. */
  conversionRate: z.string(),
  byStage: z.record(LeadStageSchema, z.number().int().nonnegative()),
  bySource: z.record(LeadSourceSchema, z.number().int().nonnegative()),
  byOwner: z.array(
    z.strictObject({
      accountId: z.string(),
      total: z.number().int().nonnegative(),
      won: z.number().int().nonnegative(),
      /** Workload (2026-10-22): open leads, of them interested, and follow-ups due by today. */
      open: z.number().int().nonnegative(),
      interested: z.number().int().nonnegative(),
      dueFollowUps: z.number().int().nonnegative(),
    }),
  ),
  /** Lead counts per interest — the same stage groups the list filters by. */
  byInterest: z.record(LeadInterestSchema, z.number().int().nonnegative()),
  openByAge: z.record(z.enum(LEAD_AGE_BANDS), z.number().int().nonnegative()),
  /** Open leads with no activity recorded since they were created. */
  untouchedOpenLeads: z.number().int().nonnegative(),
  /** Leads (not people) per follow-up status; `unmapped` counts those still needing one. */
  byFollowUpStatus: z.record(z.string(), z.number().int().nonnegative()),
  /** Leads awaiting reassignment, and Cold Call leads still open — counted as leads, as listed. */
  recycleAwaiting: z.number().int().nonnegative(),
  coldCallOpen: z.number().int().nonnegative(),
});
export type CrmDashboard = z.infer<typeof CrmDashboardSchema>;

/* ----------------------------------------------------------------- opportunity */

/**
 * CRM-OPP-001: an opportunity is one prospective sale to one customer — several can run at once, for
 * different projects or unit types. It is its own record: a lead is an enquiry, a reservation is a
 * commitment, and an opportunity is the negotiation in between.
 *
 * The stage codes are the product's; their labels are relabelled per deployment and any win
 * probability per stage is configuration (`BD-27`) — none is shipped.
 */
export const OPPORTUNITY_STAGES = [
  'discovery',
  'unitSelection',
  'proposal',
  'negotiation',
  'reservation',
  'won',
  'lost',
] as const;
export const OpportunityStageSchema = z.enum(OPPORTUNITY_STAGES);
export type OpportunityStage = z.infer<typeof OpportunityStageSchema>;

/** The stages a person moves an opportunity between. `reservation` and `won` are set by sales. */
export const OPEN_OPPORTUNITY_STAGES = [
  'discovery',
  'unitSelection',
  'proposal',
  'negotiation',
] as const;
export const TERMINAL_OPPORTUNITY_STAGES: readonly OpportunityStage[] = ['won', 'lost'];

/**
 * Permitted moves. Backwards moves in the middle are allowed, as for leads. `reservation` is entered
 * only by creating a reservation, and `won` only by activating its contract; a cancelled reservation
 * returns the opportunity to `negotiation`. `lost` needs a reason and may be reopened.
 */
export const OPPORTUNITY_TRANSITIONS: Readonly<
  Record<OpportunityStage, readonly OpportunityStage[]>
> = {
  discovery: ['unitSelection', 'proposal', 'negotiation', 'reservation', 'lost'],
  unitSelection: ['discovery', 'proposal', 'negotiation', 'reservation', 'lost'],
  proposal: ['discovery', 'unitSelection', 'negotiation', 'reservation', 'lost'],
  negotiation: ['unitSelection', 'proposal', 'reservation', 'lost'],
  reservation: ['negotiation', 'won', 'lost'],
  won: [],
  lost: ['discovery'],
};

export function canTransitionOpportunity(from: OpportunityStage, to: OpportunityStage): boolean {
  return OPPORTUNITY_TRANSITIONS[from].includes(to);
}

/** Stages only the sales workflow may set; a person asking for them is refused. */
export const SYSTEM_OPPORTUNITY_STAGES: readonly OpportunityStage[] = ['reservation', 'won'];

export const OpportunitySchema = z.strictObject({
  opportunityId: RecordIdSchema,
  customerId: RecordIdSchema,
  /** The enquiry it came from, when there was one. */
  leadId: RecordIdSchema.optional(),
  /** Attribution copied from the lead and never changed afterwards (CRM-LEAD-002, MM §9 rule 14). */
  source: LeadSourceSchema.optional(),
  campaignId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  propertyType: PropertyTypeSchema.optional(),
  usageType: UsageTypeSchema.optional(),
  budgetMin: MoneySchema.optional(),
  budgetMax: MoneySchema.optional(),
  /** What the sale is expected to be worth, as entered. Never computed from a guessed probability. */
  expectedValue: MoneySchema.optional(),
  expectedCloseOn: BusinessDateSchema.optional(),
  stage: OpportunityStageSchema,
  /**
   * The configured win probability for the stage, as a decimal percentage string. Absent unless the
   * deployment configured one (`sales.opportunityStageProbabilities`, `BD-27`).
   */
  probability: z.string().optional(),
  lostReason: z.string().max(500).optional(),
  lostReasonCode: ReasonCodeSchema.optional(),
  /** The reservation that took it to `reservation`, and the contract that won it. Opaque to CRM. */
  reservationId: RecordIdSchema.optional(),
  contractId: RecordIdSchema.optional(),
  notes: NoteSchema.optional(),
  ownerAccountId: z.string().min(1).max(200),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  stageChangedAt: InstantSchema,
  closedAt: InstantSchema.optional(),
  /** Set while archived (ADR-0047). Only a closed opportunity — won or lost — is archived. */
  archived: ArchiveMarkSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Opportunity = z.infer<typeof OpportunitySchema>;

const opportunityDetails = {
  projectId: RecordIdSchema.optional(),
  propertyType: PropertyTypeSchema.optional(),
  usageType: UsageTypeSchema.optional(),
  budgetMin: MoneySchema.optional(),
  budgetMax: MoneySchema.optional(),
  expectedValue: MoneySchema.optional(),
  expectedCloseOn: BusinessDateSchema.optional(),
  notes: NoteSchema.optional(),
};

export const CreateOpportunitySchema = z.strictObject({
  customerId: RecordIdSchema,
  ...opportunityDetails,
  /** Naming another owner needs `crm.opportunity.assign`; without it the creator owns it. */
  ownerAccountId: z.string().min(1).max(200).optional(),
});
export type CreateOpportunity = z.infer<typeof CreateOpportunitySchema>;

export const UpdateOpportunitySchema = z.strictObject({
  ...opportunityDetails,
  expectedVersion: z.number().int().positive(),
});
export type UpdateOpportunity = z.infer<typeof UpdateOpportunitySchema>;

export const ChangeOpportunityStageSchema = z.strictObject({
  stage: OpportunityStageSchema,
  reason: z.string().trim().max(500).optional(),
  reasonCode: ReasonCodeSchema.optional(),
  expectedVersion: z.number().int().positive(),
});
export type ChangeOpportunityStage = z.infer<typeof ChangeOpportunityStageSchema>;

export const AssignOpportunitySchema = TransferOwnershipSchema;

/** What a lead's conversion may also open: an opportunity from the lead's own preferences. */
export const ConvertLeadSchema = z.strictObject({
  opportunity: z
    .strictObject({
      expectedValue: MoneySchema.optional(),
      expectedCloseOn: BusinessDateSchema.optional(),
      notes: NoteSchema.optional(),
    })
    .optional(),
});
export type ConvertLead = z.infer<typeof ConvertLeadSchema>;

/** CRM-LEAD-005. Idempotent: converting a converted lead returns the customer it already has. */
export const ConvertLeadResultSchema = z.strictObject({
  lead: LeadSchema,
  customer: CustomerSchema,
  /** False when the lead was already converted, or an existing customer matched its phone. */
  customerCreated: z.boolean(),
  /** The opportunity opened by this conversion, when one was asked for. */
  opportunity: OpportunitySchema.optional(),
});

export const OPPORTUNITY_PAGE_SIZE_DEFAULT = 25;
export const OPPORTUNITY_PAGE_SIZE_MAX = 100;

export const OpportunityQuerySchema = z.strictObject({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(OPPORTUNITY_PAGE_SIZE_MAX)
    .default(OPPORTUNITY_PAGE_SIZE_DEFAULT),
  cursor: z.string().min(1).max(200).optional(),
  stage: OpportunityStageSchema.optional(),
  /** `open` is every stage that is neither won nor lost. */
  status: z.enum(['open', 'won', 'lost']).optional(),
  customerId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  ownerAccountId: z.string().min(1).max(200).optional(),
  /** Active records (the default), archived ones, or both (ADR-0047). */
  archive: ArchiveViewSchema.optional(),
});
export type OpportunityQuery = z.infer<typeof OpportunityQuerySchema>;

export const OpportunityPageSchema = z.strictObject({
  items: z.array(OpportunitySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type OpportunityPage = z.infer<typeof OpportunityPageSchema>;

/**
 * The pipeline in the actor's scope (CRM-REPORT-001): counts and expected value per stage, summed by
 * the database in `Decimal128` and never across currencies. A weighted value appears only when win
 * probabilities are configured; the product does not forecast on a guess.
 */
export const OpportunitySummarySchema = z.strictObject({
  byStage: z.array(
    z.strictObject({
      stage: OpportunityStageSchema,
      count: z.number().int().nonnegative(),
      expectedValue: z.array(MoneySchema),
    }),
  ),
  open: z.number().int().nonnegative(),
  /** Present only with configured probabilities: Σ expected value × probability, per currency. */
  weightedOpenValue: z.array(MoneySchema).optional(),
  probabilitiesConfigured: z.boolean(),
});
export type OpportunitySummary = z.infer<typeof OpportunitySummarySchema>;

export const CustomerListSchema = CustomerPageSchema;
export const ActivityListSchema = z.strictObject({ items: z.array(ActivitySchema) });

export const CRM_AUDIT_ACTIONS = {
  leadCreated: 'crm.lead.created',
  leadUpdated: 'crm.lead.updated',
  leadStageChanged: 'crm.lead.stageChanged',
  leadStageRefused: 'crm.lead.stageRefused',
  leadAssigned: 'crm.lead.assigned',
  /** Work refused to a colleague who could not do it — a lead assignment or a customer transfer. */
  ownerAssignmentRefused: 'crm.owner.assignmentRefused',
  leadQualified: 'crm.lead.qualified',
  leadConverted: 'crm.lead.converted',
  activityRecorded: 'crm.activity.recorded',
  customerCreated: 'crm.customer.created',
  customerUpdated: 'crm.customer.updated',
  customerCorrected: 'crm.customer.corrected',
  customerOwnerTransferred: 'crm.customer.ownerTransferred',
  consentRecorded: 'crm.customer.consentRecorded',
  customerMerged: 'crm.customer.merged',
  distributionRuleSaved: 'crm.distributionRule.saved',
  distributionRuleRetired: 'crm.distributionRule.retired',
  opportunityCreated: 'crm.opportunity.created',
  opportunityUpdated: 'crm.opportunity.updated',
  opportunityStageChanged: 'crm.opportunity.stageChanged',
  opportunityStageRefused: 'crm.opportunity.stageRefused',
  opportunityAssigned: 'crm.opportunity.assigned',
  leadFollowUpStatusChanged: 'crm.lead.followUpStatusChanged',
  leadFollowUpStatusMapped: 'crm.lead.followUpStatusMapped',
  leadBooked: 'crm.lead.booked',
  leadBookingEnded: 'crm.lead.bookingEnded',
  leadRecycled: 'crm.lead.recycled',
  leadColdCallAssigned: 'crm.lead.coldCallAssigned',
  autoRecycleRuleSaved: 'crm.autoRecycle.saved',
} as const;

/* -------------------------------------------------------- lead distribution (CRM-ASSIGN-002) */

export const DISTRIBUTION_METHODS = ['roundRobin', 'fewestOpenLeads'] as const;
export const DISTRIBUTION_RULE_STATES = ['active', 'superseded', 'retired'] as const;
/** Why a member was passed over for one lead — kept with the lead as its explanation. */
export const DISTRIBUTION_SKIP_REASONS = [
  'unavailable',
  'unknown',
  'inactive',
  'cannotSeeLeads',
  'notPlaced',
  'otherBranch',
] as const;

export const DistributionMemberSchema = z.strictObject({
  accountId: z.string().min(1).max(200),
  /** Off while the person is away: passed over, never removed (absence handling). */
  available: z.boolean(),
});

/**
 * A distribution rule (CRM-ASSIGN-002, BD-25): for new leads of one branch — of the listed sources
 * and projects, or any — the first owner is chosen among the members by round robin or by the fewest
 * open leads. Rules are tried in priority order, the first that matches decides. Every change is a new
 * version; the lead records the version that chose its owner.
 */
export const DistributionRuleSchema = z.strictObject({
  ruleId: RecordIdSchema,
  version: z.number().int().positive(),
  state: z.enum(DISTRIBUTION_RULE_STATES),
  name: z.string().trim().min(2).max(120),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  priority: z.number().int().min(1).max(999),
  sources: z.array(z.enum(LEAD_SOURCES)).max(20),
  projectIds: z.array(RecordIdSchema).max(100),
  method: z.enum(DISTRIBUTION_METHODS),
  members: z.array(DistributionMemberSchema).min(1).max(100),
  reason: z.string().max(500),
  createdBy: z.string().max(200),
  createdAt: InstantSchema,
  retiredAt: InstantSchema.optional(),
});
export type DistributionRule = z.infer<typeof DistributionRuleSchema>;

export const SaveDistributionRuleSchema = z.strictObject({
  name: z.string().trim().min(2).max(120),
  branchId: RecordIdSchema,
  priority: z.number().int().min(1).max(999),
  sources: z.array(z.enum(LEAD_SOURCES)).max(20).default([]),
  projectIds: z.array(RecordIdSchema).max(100).default([]),
  method: z.enum(DISTRIBUTION_METHODS),
  members: z.array(DistributionMemberSchema).min(1).max(100),
  reason: z.string().trim().min(3).max(500),
  /** The version being replaced; absent when creating a rule. */
  expectedVersion: z.number().int().positive().optional(),
});
export type SaveDistributionRule = z.infer<typeof SaveDistributionRuleSchema>;

export const RetireDistributionRuleSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

/** The explanation kept with a lead whose first owner a rule chose — or tried to. */
export const LeadDistributionSchema = z.strictObject({
  ruleId: RecordIdSchema,
  ruleVersion: z.number().int().positive(),
  method: z.enum(DISTRIBUTION_METHODS),
  outcome: z.enum(['assigned', 'noEligibleMember']),
  /** Round robin: the turn this lead took. */
  turn: z.number().int().nonnegative().optional(),
  /** Fewest open leads: the chosen member's open leads when chosen. */
  openLeads: z.number().int().nonnegative().optional(),
  assignedToAccountId: z.string().max(200).optional(),
  skipped: z.array(
    z.strictObject({
      accountId: z.string().max(200),
      reason: z.enum(DISTRIBUTION_SKIP_REASONS),
    }),
  ),
  decidedAt: InstantSchema,
});
export type LeadDistribution = z.infer<typeof LeadDistributionSchema>;

export const DistributionPreviewRequestSchema = z.strictObject({
  branchId: RecordIdSchema,
  source: z.enum(LEAD_SOURCES),
  projectId: RecordIdSchema.optional(),
});

/** Which rule would decide a lead now, and for whom — computed without taking a turn. */
export const DistributionPreviewSchema = z.strictObject({
  matched: z.boolean(),
  rule: DistributionRuleSchema.optional(),
  outcome: z.enum(['assigned', 'noEligibleMember', 'noRule']),
  assignedToAccountId: z.string().max(200).optional(),
  skipped: z.array(
    z.strictObject({
      accountId: z.string().max(200),
      reason: z.enum(DISTRIBUTION_SKIP_REASONS),
    }),
  ),
});
export type DistributionPreview = z.infer<typeof DistributionPreviewSchema>;
